import React, {useEffect} from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import { Grid, Tab, Select, Input, MenuItem, Checkbox, ListItemText } from '@material-ui/core';
import { TabContext, TabList, TabPanel } from "@material-ui/lab";
import {
  fetchDashboardActiveWorkflowTasks,
  fetchDashboardAvailableTasks,
  fetchDashboardTaskCountByStatus,
  fetchDashboardTasks,
  setDashboardChannels,
  setSelectedTab
} from 'store/actions';
import Stack from "components/layout/Stack";
import Inventory from "./Inventory";
import Workload from "./workload/Workload";
import { Constants } from "../../utilities/constants";

const Dashboard = ({
  classes,
  match,
  selectedTab,
  setSelectedTab,
  appSettings,
  myTasksSelectedUsers,
  currentUser,
  activeWorkflowTasks,
  setDashboardChannels,
  dashboardChannels,
  fetchDashboardTasks,
  rules,
  fetchDashboardAvailableTasks,
  fetchDashboardActiveWorkflowTasks,
  fetchDashboardTaskCountByStatus,
  dashboardTasks,
  dashboardAvailableTasks,
}) => {
  const numbersOfDaysBackwards = 30;
  myTasksSelectedUsers = myTasksSelectedUsers || [currentUser.id];

  useEffect(() => {
    if (dashboardTasks.length === 0) {
      fetchDashboardTasks(dashboardChannels, myTasksSelectedUsers);
    }
    if (dashboardAvailableTasks.length === 0) {
      fetchDashboardAvailableTasks(dashboardChannels);
    }
    if (activeWorkflowTasks.length === 0) {
      fetchDashboardActiveWorkflowTasks(dashboardChannels);
    }
    fetchDashboardTaskCountByStatus(dashboardChannels, numbersOfDaysBackwards);
  }, [rules]);

  function handleTabChange(event, value) {
    setSelectedTab(value);
  }

  function getChannelsFilter() {
    const channels = Object.keys(appSettings.Channels)
        .filter(channel => [Constants.Channel.MLS, Constants.Channel.OFFMARKET].includes(channel))
        .map(channel => {
          return {name: appSettings.Channels[channel].displayName, key: channel};
    });

    return (
      <Select
        multiple
        value={dashboardChannels}
        onChange={handleChannelChange}
        input={<Input />}
        style={{ width: '100%', marginBottom: 4 }}
        renderValue={(selected) => selected.map(key => channels.find(channel => channel.key === key)?.name).join(', ')}
        MenuProps={{
          style: { maxHeight: 600 },
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
        }}
      >
        {channels.map((channel) => (
          <MenuItem key={channel.key} value={channel.key} dense>
            <Checkbox checked={dashboardChannels.indexOf(channel.key) > -1} />
            <ListItemText primary={channel.name} />
          </MenuItem>
        ))}
      </Select>
    );
  }

  function handleChannelChange(e) {
    const newChannels = e.target.value;
    if (e.oldValue === newChannels) {
      return;
    }

    setDashboardChannels(newChannels);

    fetchDashboardActiveWorkflowTasks(newChannels);
    fetchDashboardTasks(newChannels, myTasksSelectedUsers);
    fetchDashboardAvailableTasks(newChannels);
    fetchDashboardTaskCountByStatus(newChannels, numbersOfDaysBackwards);
  }

  function refreshWorkloadData() {
    fetchDashboardActiveWorkflowTasks(dashboardChannels);
    fetchDashboardTasks(dashboardChannels, myTasksSelectedUsers);
    fetchDashboardAvailableTasks(dashboardChannels);
    fetchDashboardTaskCountByStatus(dashboardChannels, numbersOfDaysBackwards);
  }

    return (
      <>
        <Stack className={classes.root} style={!match
          ? { position: 'absolute', left: '-100vw', visibility: 'hidden' }
          : null
        }>
          <div className={classes.headerContainer}>
            <Grid container spacing={1}>
              <TabContext value={selectedTab}>
                <Grid item xs={9}>
                  <TabList onChange={handleTabChange} className={classes.tabsRoot}>
                    <Tab label="Workload" value="workload" className={classes.roundedTab} />
                    <Tab label="Rental Inventory" value="rentalInventory" className={classes.roundedTab} />
                    <Tab label="Retail Inventory" value="retailInventory" className={classes.roundedTab} />
                  </TabList >
                </Grid>
                <Grid item xs={3}>
                  {getChannelsFilter()}
                </Grid>
                <Grid item xs={12}>
                  <TabPanel value='workload' index={0} className={classes.tabPanel}>
                    <Workload
                      refreshWorkloadData={refreshWorkloadData}
                      dashboardAvailableTasks={dashboardAvailableTasks}
                      dashboardChannels={dashboardChannels}
                      currentUser={currentUser}
                      selectedUsers={myTasksSelectedUsers}
                      dashboardTasks={dashboardTasks}
                    />
                  </TabPanel>
                  <TabPanel value='rentalInventory' index={1} className={classes.tabPanel}>
                    <Inventory
                      inventoryType='Rental'
                    />
                  </TabPanel>
                  <TabPanel value='retailInventory' index={2} className={classes.tabPanel}>
                    <Inventory
                      inventoryType='Retail'
                    />
                  </TabPanel>
                </Grid>
              </TabContext>
            </Grid>
          </div>
        </Stack>
      </>
    )
  }

  const styles = theme => ({
    root: {
      width: '100%',
      height: '100%',
      padding: '0px 15px'
    },
    headerContainer: {
      display: 'flex',
      alignItems: 'left',
      justifyContent: 'space-between',
      marginTop: 30,
    },
    gridRoundedContainer:{
      border: '1px solid #000',
      borderRadius: '8px'
    },
    gridHeaderBackground: {
      textAlign: 'left',
      background: '#B6D2ED',
      color: theme.palette.text.primary,
      fontWeight:'bold',
      padding: '5px 0px 2px 8px',
      borderBottom: '1px solid #000'
    },
    tabsRoot: {
      minHeight: 0
    },
    roundedTab: {
      backgroundColor: '#e6e6e6',
      borderRadius: '8px 8px 0 0',
      boxShadow: '0px 1px 5px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.12)',
      minHeight: 0,
      minWidth: 0,
      padding: '8px 16px'
    },
    tabPanel: {
      padding: 2,
    },
  });

  const mapStateToProps = (state) => ({
    selectedTab: state.dashboardState.dashboardSelectedTab,
    appSettings: state.appState.appSettings,
    myTasksSelectedUsers: state.dashboardState.myTasksSelectedUsers,
    currentUser: state.appState.user,
    dashboardChannels: state.dashboardState.dashboardChannels,
    filter: state.dashboardState.filter,
    rules: state.dashboardState.rules,
    dashboardTasks: state.dashboardState.dashboardTasks,
    dashboardAvailableTasks: state.dashboardState.dashboardAvailableTasks,
    activeWorkflowTasks: state.dashboardState.activeWorkflowTasks,
  });

  const mapDispatchToProps = (dispatch) => ({
    setSelectedTab: (tab) => dispatch(setSelectedTab(tab)),
    setDashboardChannels: (channels) => dispatch(setDashboardChannels(channels)),
    fetchDashboardTasks: (channels, selectedUsers) => dispatch(fetchDashboardTasks(channels, selectedUsers)),
    fetchDashboardAvailableTasks: (channels) => dispatch(fetchDashboardAvailableTasks({channels})),
    fetchDashboardActiveWorkflowTasks: (channel) => dispatch(fetchDashboardActiveWorkflowTasks({channel})),
    fetchDashboardTaskCountByStatus: (channel, numbersOfDaysBackwards) => dispatch(fetchDashboardTaskCountByStatus({channel, numbersOfDaysBackwards})),
  });

  export default compose(
    withRouter,
    withStyles(styles),
    connect(
      mapStateToProps,
      mapDispatchToProps
    )
  )(Dashboard);
